import type { Icon } from '@change/ds-icons';
import { EmailIcon, FacebookIcon, TwitterIcon } from '@change/ds-icons';

import { SocialSharing, SocialSharingGroup } from '@change/ds-core-ui/next';

import type { TranslationKey } from '@change-corgi/core/i18n';
import { translationKey } from '@change-corgi/core/i18n';
import { useI18n } from '@change-corgi/core/react/i18n';

import { useShareLinks } from './hooks/useShareLinks';
import type { Channel } from './hooks/useShareLinks';

const socialIcons: Readonly<
	Record<
		Channel,
		Readonly<{
			icon: React.FC<Icon>;
			displayLabel: TranslationKey;
		}>
	>
> = {
	twitter: {
		icon: TwitterIcon,
		displayLabel: translationKey('corgi.petition_guides_article.page_heading.share.twitter.button_label'),
	},
	facebook: {
		icon: FacebookIcon,
		displayLabel: translationKey('corgi.petition_guides_article.page_heading.share.facebook.button_label'),
	},
	email: {
		icon: EmailIcon,
		displayLabel: translationKey('corgi.petition_guides_article.page_heading.share.email.button_label'),
	},
} as const;

export function ShareLinks() {
	const {
		data: { shareActions },
	} = useShareLinks();

	const { translate } = useI18n();

	const generateIcon = (channel: Channel | undefined, action: () => Promise<boolean>) => {
		const { icon: IconComponent, displayLabel } = socialIcons[channel as keyof typeof socialIcons];
		const variant = channel === 'email' ? 'default' : channel;
		return (
			<SocialSharing
				data-testid={`article-share-${channel}`}
				data-qa={`article-share-${channel}`}
				key={`article-share-${channel}`}
				aria-label={translate(displayLabel)}
				className="w-12"
				icon={<IconComponent />}
				label=""
				onClick={action}
				size="large"
				variant={variant}
			/>
		);
	};

	return (
		<SocialSharingGroup layout="inline" className="gap-4">
			{shareActions.map(({ channel, action }) => {
				return generateIcon(channel, action);
			})}
		</SocialSharingGroup>
	);
}
